import React from "react";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import { X, Download, AlertCircle, CheckCircle } from "lucide-react";
import { type ExportProgress } from "../types";

interface ExportProgressModalProps {
  progress: ExportProgress;
  onCancel: () => void;
  onClose: () => void;
  isOpen: boolean;
}

export function ExportProgressModal({
  progress,
  onCancel,
  onClose,
  isOpen,
}: ExportProgressModalProps) {
  if (!isOpen) return null;

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const getProgressPercentage = () => {
    if (progress.totalRecords === 0) return 0;
    return Math.round(
      (progress.processedRecords / progress.totalRecords) * 100
    );
  };

  const getStageMessage = () => {
    switch (progress.stage) {
      case "preparing":
        return "Preparing export...";
      case "fetching":
        return `Fetching data (${progress.completedBatches}/${progress.totalBatches} batches)`;
      case "processing":
        return "Processing data for export...";
      case "generating":
        return "Generating CSV file...";
      case "complete":
        return "Export completed successfully!";
      case "error":
        return `Export failed: ${progress.error}`;
      default:
        return "Exporting...";
    }
  };

  const getStageIcon = () => {
    switch (progress.stage) {
      case "complete":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Download className="h-5 w-5 text-blue-500 animate-pulse" />;
    }
  };

  const showCancelButton =
    progress.isExporting &&
    progress.stage !== "complete" &&
    progress.stage !== "error";
  const showCloseButton =
    progress.stage === "complete" || progress.stage === "error";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-lg mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {getStageIcon()}
            <h3 className="text-lg font-semibold">Exporting Data</h3>
          </div>

          {showCloseButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>{getStageMessage()}</span>
              <span>{getProgressPercentage()}%</span>
            </div>
            <Progress value={getProgressPercentage()} className="h-2" />
          </div>

          {/* Detailed Progress Info */}
          {progress.isExporting && progress.stage === "fetching" && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Records:</span>
                <span className="ml-2 font-mono">
                  {progress.processedRecords.toLocaleString()} /{" "}
                  {progress.totalRecords.toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Batches:</span>
                <span className="ml-2 font-mono">
                  {progress.completedBatches} / {progress.totalBatches}
                </span>
              </div>
              {progress.estimatedTimeRemaining && (
                <div className="col-span-2">
                  <span className="text-gray-500">Est. time remaining:</span>
                  <span className="ml-2 font-mono">
                    {formatTime(progress.estimatedTimeRemaining)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2">
            {showCancelButton && (
              <Button
                variant="outline"
                onClick={onCancel}
                className="text-red-600 hover:text-red-700"
              >
                Cancel Export
              </Button>
            )}

            {showCloseButton && <Button onClick={onClose}>Close</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
